import React, { useEffect, useRef, useState } from 'react';
import { each } from 'lodash-es';

import { aiFeaturesEnabled } from 'models/ai_features';
import AutocompleteFieldConfig from 'components/Explorer/models/autocomplete_field_config';
import { CountriesField, DateField } from 'components/Explorer/Form';
import config from 'util/site_config';
import FilterSet from 'components/Explorer/models/filter_set';
import * as QueryParams from 'components/Explorer/models/query_params';
import useQueryParams from 'hooks/useQueryParams';
import useNavigateParams from 'hooks/useNavigateParams';
import MentionSourcesFilter from '../MentionSourcesFilter';
import SentimentFilter from '../SentimentFilter';

const buildTypeOptions = () => {
  const options = {};
  each(
    config.mentionsTabPostTypes,
    (type) =>
      (options[type] = I18n.t(type, {
        scope: 'Explorer.Mentions.FilterPanel.select_box_mention_source_post_types',
        default: type
      }))
  );
  return options;
};

const buildAutocompleteFieldConfig = () => {
  const acf = new AutocompleteFieldConfig();
  acf.id = 'mention_sources';
  acf.name = 'mention_sources[]';
  acf.type = 'mention_sources';
  acf.placeholder = I18n.t('Explorer.Mentions.FilterPanel.fields.mention_sources.placeholder');
  return acf;
};

const FilterPanel = () => {
  const params = useQueryParams();
  const navigateParams = useNavigateParams();

  const [filterSet, setFilterSet] = useState(() => new FilterSet(params.filters));
  const [highlights, setHighlights] = useState(params.filters.highlights === 'true');
  const [minimumDate, setMinimumDate] = useState(null);
  const [maximumDate, setMaximumDate] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const mentionedAfterField = useRef(null);
  const mentionedBeforeField = useRef(null);
  const countriesField = useRef(null);
  const mentionSourcesField = useRef(null);
  const sentimentField = useRef(null);

  const clearAutocompleteFilters = (fs) => {
    if (countriesField.current && fs.get('countries[]').length === 0) countriesField.current.clearSelected();
    if (mentionSourcesField.current && fs.get('mention_sources[]').length === 0)
      mentionSourcesField.current.clearSelected();
  };

  // Re-derive filterSet when URL filters change.
  useEffect(() => {
    const fs = new FilterSet(params.filters);
    setFilterSet(fs);
    setHighlights(params.filters.highlights === 'true');
    clearAutocompleteFilters(fs);
  }, [JSON.stringify(params.filters)]);

  const typeOptions = buildTypeOptions();
  const autocompleteFieldConfig = buildAutocompleteFieldConfig();

  const areDatesValid = () => {
    if (!mentionedAfterField.current?.checkValidity() || !mentionedBeforeField.current?.checkValidity()) {
      setErrorMessage('Start date must be before end date');
      return false;
    }
    setErrorMessage(null);
    return true;
  };

  const applySearch = () => {
    if (!areDatesValid()) return;
    Analytics.trackEvent('mentions-apply-filter');
    const newParams = QueryParams.withFilters(params, filterSet.populatedFilters);
    if (QueryParams.buildSearch(newParams) !== window.location.search.replace('?', '')) {
      navigateParams(newParams);
    }
  };

  const clearSearch = () => {
    mentionedBeforeField.current?.clear();
    mentionedAfterField.current?.clear();
    countriesField.current?.clear();
    mentionSourcesField.current?.clear();
    sentimentField.current?.clear();
  };

  const resetSearch = () => {
    clearSearch();
    applySearch();
  };

  const toggleHighlights = () => {
    const next = !highlights;
    Analytics.trackEvent('mentions-set-highlights', { enabled: next });
    setHighlights(next);
    if (next) {
      mentionSourcesField.current?.clear();
      filterSet.set('highlights', 'true');
    } else {
      filterSet.clear('highlights');
    }
    navigateParams(QueryParams.withFilters(params, filterSet.populatedFilters));
  };

  return (
    <div className="Explorer-Form Explorer-Mentions-FilterPanel">
      <div className="Explorer-Mentions-FilterPanel-row">
        <MentionSourcesFilter
          sourcesTypes={typeOptions}
          filterSet={filterSet}
          queryFilter="mentionSourcesForFilter"
          assignRef={(f) => (mentionSourcesField.current = f)}
          disabled={highlights}
          dropdownSourcesType="post_type"
          autocompleteFieldConfig={autocompleteFieldConfig}
        />
        {aiFeaturesEnabled() && (
          <SentimentFilter
            assignRef={(f) => (sentimentField.current = f)}
            filterSet={filterSet}
            isDisabled={highlights}
          />
        )}
        <button
          className={`Explorer-Mentions-Highlights-toggle ${highlights ? 'active' : 'not-active'}`}
          onClick={toggleHighlights}
        >
          {I18n.t('Explorer.Mentions.FilterPanel.buttons.highlights')}
        </button>
      </div>
      <div className="Explorer-Mentions-FilterPanel-row">
        <div className="Explorer-Mentions-FilterPanel-date-range">
          <div className="Explorer-FilterPanel-field">
            <DateField
              fieldID="mentioned_after"
              fieldName="mentioned_after"
              label={I18n.t('Explorer.Mentions.FilterPanel.fields.mentioned_after.title')}
              filterSet={filterSet}
              placeholder={I18n.t('Explorer.Mentions.FilterPanel.fields.mentioned_after.placeholder')}
              onChangeCallback={setMinimumDate}
              maximumDate={maximumDate || filterSet.get('mentioned_before')}
              ref={mentionedAfterField}
            />
          </div>
          <div className="Explorer-FilterPanel-field">
            <DateField
              fieldID="mentioned_before"
              fieldName="mentioned_before"
              label={I18n.t('Explorer.Mentions.FilterPanel.fields.mentioned_before.title')}
              filterSet={filterSet}
              placeholder={I18n.t('Explorer.Mentions.FilterPanel.fields.mentioned_before.placeholder')}
              onChangeCallback={setMaximumDate}
              minimumDate={minimumDate || filterSet.get('mentioned_after')}
              ref={mentionedBeforeField}
            />
          </div>
        </div>
        <CountriesField
          filterSet={filterSet}
          label={I18n.t('Explorer.Mentions.FilterPanel.fields.countries.title')}
          placeholder={I18n.t('Explorer.Form.CountriesField.placeholder')}
          onChangeCallback={() => Analytics.trackEvent('mentions-update-countries')}
          ref={countriesField}
        />
        <div className="Explorer-FilterPanel-buttons">
          <button className="apply-button" onClick={applySearch}>
            {I18n.t('Explorer.Mentions.FilterPanel.buttons.apply')}
          </button>
          <button className="clear-button" onClick={resetSearch}>
            {I18n.t('Explorer.Mentions.FilterPanel.buttons.reset')}
          </button>
        </div>
      </div>
      {errorMessage && <div className="Explorer-FilterPanel-warning-message">{errorMessage}</div>}
    </div>
  );
};

export default FilterPanel;
